import { useQuery } from "@tanstack/react-query";
import round from "lodash.round";

import { type ResolvedRange, resolveRange } from "~/modules/Dashboard/AppStats/hooks/comparisonRange";
import { type DateRange } from "~/modules/Dashboard/AppStats/hooks/types";
import { ALL_PRODUCTS_ITEM_ID } from "~/modules/Dashboard/AppStats/hooks/useProductsList";
import { productApi } from "~/store/initializeStore";

export interface CheckoutSessionDailyStats {
  date: string;
  views: number;
  purchased: number;
}

export interface CheckoutSessionStats {
  views: number;
  uniqueVisitors: number;
  purchased: number;
  conversion: number; // %
  perDay: CheckoutSessionDailyStats[];
}

const DATE_FORMAT = "YYYY-MM-DD";

const fetchStats = async (products: string[], [start, end]: ResolvedRange) => {
  const isAllProducts = products.length === 1 && products[0] === ALL_PRODUCTS_ITEM_ID;

  const response = await productApi.get<CheckoutSessionStats>("/checkout-sessions/stats", {
    params: {
      startDate: start.format(DATE_FORMAT),
      endDate: end.format(DATE_FORMAT),
      ...(isAllProducts ? {} : { productIds: products.join(",") }),
    },
  });

  return response.data;
};

const rangeKey = (range: ResolvedRange | null) => range?.map((date) => date.format(DATE_FORMAT)) ?? null;

export const useCheckoutSessionStats = (
  products: string[],
  range: DateRange,
  comparisonRange: ResolvedRange | null,
) => {
  const currentRange = resolveRange(range);

  const { data, isLoading } = useQuery(["useCheckoutSessionStats", products, rangeKey(currentRange)], {
    enabled: !!products.length && !!currentRange,
    keepPreviousData: true,
    queryFn: () => fetchStats(products, currentRange!),
  });

  const { data: previousData } = useQuery(["usePreviousCheckoutSessionStats", products, rangeKey(comparisonRange)], {
    enabled: !!products.length && !!comparisonRange,
    keepPreviousData: true,
    queryFn: () => fetchStats(products, comparisonRange!),
  });

  return {
    isLoading,
    stats: data ?? null,
    views: data?.views ?? 0,
    viewsPrevious: previousData ? previousData.views : null,
    uniqueVisitors: data?.uniqueVisitors ?? 0,
    purchased: data?.purchased ?? 0,
    conversion: data?.conversion ?? 0,
    conversionPrevious: previousData ? previousData.conversion : null,
    sessionChange: data && previousData?.views ? round((data.views / previousData.views) * 100 - 100) : null,
  };
};
